import { useMemo, useState, ReactNode, CSSProperties } from 'react';
import {
  Button,
  IconButton,
  Stack,
  Tabs,
  Tab,
  Typography,
  SxProps,
  Theme,
} from '@mui/material';
import { AddCircle, DeleteForever, Edit } from '@mui/icons-material';
import AddOrEditNotesDialog from './add-edit-notes-dialog';
import { useNotificationContext } from './notification';
import {
  dateFormats,
  formatDateTime,
  renderTextLines,
} from '../../shared/utils';
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const TableWrapper = require('./table').default as any;
import { NotesAPI } from '../../api/notes';
import { BaseOption } from '../../api/options';
import { dataTypes } from './table-helpers';

export interface Note {
  note_id: number;
  note: string;
  topic: string;
  notepad_class: string;
  notepad_class_desc: string;
  entry_date: string | null;
  created_by: string;
  areas?: string[];
  supplier_id?: string;
  supplier_name?: string;
  delete_flag?: string;
}

interface NoteWithMeta extends Note {
  isNew: boolean;
  noteEntityKey: string;
  noteType: string;
  lineNumber?: number;
}

export interface NotesData {
  notes: Note[];
  noteEntityKey: string;
  noteType: string;
  defaultArea?: string;
  lineNumber?: number;
}

interface Column {
  id: string;
  label: string | ReactNode;
  cellSx?: SxProps<Theme>;
  isSortable?: boolean;
  sortByField?: string;
  sortDataType?: unknown;
}

export interface NotesProps {
  data: NotesData;
  onNotesModified: () => void;
  filterClassOptions?: (options: BaseOption[]) => BaseOption[];
  supplierOptions?: BaseOption[];
  style?: CSSProperties;
  additionalDetailsToDisplay?: string[][];
  additionalColumns?: Column[];
  showTypeTabs?: boolean;
  hasPermissionToEdit?: boolean;
  showMoreTitle?: string;
  onShowMore?: () => void;
  additionalAddHeaderDetails?: (() => ReactNode) | null;
  canCreateNewNotes?: boolean;
  showSearch?: boolean;
  showHideNoteCheckbox?: boolean;
}

export const Notes = ({
  data,
  onNotesModified,
  filterClassOptions,
  supplierOptions,
  style = {},
  additionalDetailsToDisplay = [],
  additionalColumns = [
    { id: 'topic', label: 'Topic', cellSx: { minWidth: '150px' } },
  ],
  showTypeTabs = false,
  hasPermissionToEdit = true,
  showMoreTitle,
  onShowMore,
  additionalAddHeaderDetails = null,
  canCreateNewNotes = true,
  showSearch = false,
  showHideNoteCheckbox = false,
}: NotesProps) => {
  const { openDialog, useExceptionHandler } = useNotificationContext();
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [note, setNote] = useState<NoteWithMeta | Record<string, unknown>>({});
  const [currentTopicFilter, setCurrentTopicFilter] = useState<string | null>(
    null
  );

  const getDefaultNoteObject = (): NoteWithMeta => {
    return {
      note_id: 0,
      note: '',
      topic: '',
      notepad_class: '',
      notepad_class_desc: '',
      entry_date: null,
      created_by: '',
      // TODO: Remove this part if we use this component for other note types.
      areas: [data.defaultArea ?? ''],
      isNew: true,
      noteEntityKey: data.noteEntityKey,
      noteType: data.noteType,
      lineNumber: data.lineNumber,
    };
  };

  const handleAdd = () => {
    setNote(getDefaultNoteObject());
    setOpenEditDialog(true);
  };

  const handleEdit = (noteToEdit: Note) => {
    const editNote: NoteWithMeta = {
      ...noteToEdit,
      isNew: false,
      noteEntityKey: data.noteEntityKey,
      noteType: data.noteType,
      lineNumber: data.lineNumber,
    };
    setNote(editNote);
    setOpenEditDialog(true);
  };

  const handleDelete = (noteToDelete: Note) => {
    openDialog({
      title: `Remove Note?`,
      message: `Are you sure you want to delete the note?`,
      handleConfirm: useExceptionHandler(async () => {
        const deleteNote: NoteWithMeta = {
          ...noteToDelete,
          delete_flag: 'Y',
          isNew: false,
          noteEntityKey: data.noteEntityKey,
          noteType: data.noteType,
          lineNumber: data.lineNumber,
        };
        setNote(deleteNote);
        await NotesAPI.editNote({
          noteType: deleteNote.noteType,
          note: deleteNote,
        });
        onNotesModified();
      }),
    });
  };

  const getActionButtons = (noteElement: Note) => {
    return (
      <Stack direction={'row'} justifyContent={'flex-end'}>
        <IconButton
          color='warning'
          size='small'
          disabled={!hasPermissionToEdit}
          onClick={() => {
            handleEdit(noteElement);
          }}
        >
          <Edit />
        </IconButton>
        <IconButton
          color='error'
          size='small'
          disabled={!hasPermissionToEdit}
          onClick={() => {
            handleDelete(noteElement);
          }}
        >
          <DeleteForever />
        </IconButton>
      </Stack>
    );
  };
  const uniqueTopics = useMemo(() => {
    const types = new Set<string>();
    data.notes?.forEach((note) => {
      if (note.notepad_class_desc) {
        types.add(note.notepad_class_desc);
      }
    });
    return types;
  }, [data.notes]);

  const newRows = data.notes
    ?.filter(
      (note) =>
        !showTypeTabs ||
        !currentTopicFilter ||
        note.notepad_class_desc == currentTopicFilter
    )
    .map((note) => ({
      ...note,
      type: note.notepad_class_desc,
      title: note.notepad_class_desc,
      supplier_description: `${note.supplier_id ?? ''} - ${
        note.supplier_name ?? ''
      }`,
      additional_details: additionalDetailsToDisplay
        .map((eachLine) =>
          eachLine
            .map(
              (eachField) =>
                (note as unknown as Record<string, unknown>)[eachField]
            )
            .join(' - ')
        )
        .join('\n'),
      topic: note.topic,
      note: () => (
        <Typography
          variant='body2'
          sx={{
            height: 'auto',
            maxHeight: '700px',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {renderTextLines(note.note)}
        </Typography>
      ),
      created_by: `${note.created_by} on ${formatDateTime(
        note.entry_date,
        dateFormats.short
      )}`,
      actions: getActionButtons(note),
    }));

  const columns: Column[] = [
    ...additionalColumns,
    ...(additionalDetailsToDisplay && additionalDetailsToDisplay.length > 0
      ? [{ id: 'additional_details', label: 'Details' }]
      : []),
    {
      id: 'created_by',
      label: 'Created By',
      isSortable: true,
      sortByField: 'entry_date',
      sortDataType: dataTypes.date,
      cellSx: { maxWidth: '150px', overflowX: 'hidden' },
    },
    {
      id: 'note',
      label: 'Note',
      cellSx: { minWidth: '300px', overflowY: 'auto' },
    },
    {
      id: 'actions',
      label: (
        <Stack direction='row' alignItems='center' justifyContent='end'>
          {showMoreTitle && onShowMore ? (
            <Button title={showMoreTitle} color='primary' onClick={onShowMore}>
              <Typography
                variant='body2'
                component='span'
                color='primary'
                onClick={onShowMore}
              >
                {showMoreTitle}
              </Typography>
            </Button>
          ) : null}
          {canCreateNewNotes && (
            <IconButton
              title='Add a new Note'
              color='primary'
              disabled={!hasPermissionToEdit}
              onClick={handleAdd}
            >
              <AddCircle />
              <Typography
                variant='body2'
                sx={{ width: '98px' }}
                color='primary'
              >
                Add New Note
              </Typography>
            </IconButton>
          )}
        </Stack>
      ),
    },
  ];

  return (
    <>
      <Stack sx={{ width: '100%' }} spacing={showTypeTabs ? 2 : 0}>
        {showTypeTabs && (
          <Tabs
            value={currentTopicFilter}
            onChange={(_, val) => setCurrentTopicFilter(val)}
            variant='scrollable'
          >
            <Tab value={null} label='All' />
            {Array.from(uniqueTopics).map((type) => {
              const tabKey = type ?? 'null';
              return (
                <Tab
                  key={tabKey}
                  value={type ?? null}
                  label={type || 'Uncategorized'}
                />
              );
            })}
          </Tabs>
        )}
        <TableWrapper
          transformRows
          rows={newRows || []}
          columns={columns}
          style={style}
          showSearch={showSearch}
        />
      </Stack>
      <AddOrEditNotesDialog
        open={openEditDialog}
        setOpen={setOpenEditDialog}
        note={note}
        setNote={setNote}
        data={data}
        showTypeOptions
        onNotesModified={onNotesModified}
        filterClassOptions={filterClassOptions}
        supplierOptions={supplierOptions}
        additionalAddHeaderDetails={additionalAddHeaderDetails}
        showHideNoteCheckbox={showHideNoteCheckbox}
      />
    </>
  );
};
